import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Loader2, Eye, EyeOff } from "lucide-react";

const errorMessages: Record<string, string> = {
  invalid_credentials: "帳號或密碼錯誤",
  server_error: "伺服器發生錯誤，請稍後再試",
  network_error: "無法連線到伺服器，請檢查網路後再試",
};

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const result = await login(username, password);

    if (result.ok) {
      // Return the user to where they were before being redirected to login.
      const next = searchParams.get("next");
      navigate(next && next.startsWith("/") ? next : "/styles");
    } else {
      setError(errorMessages[result.reason] ?? "登入失敗，請稍後再試");
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ceramic-50">
      <div className="w-full max-w-md p-8 bg-white border border-ceramic-200 rounded-xl">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-ceramic-900">Fig It Admin</h1>
          <p className="text-ceramic-500 mt-2">官方模板庫管理後台</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm"
            >
              {error}
            </div>
          )}

          <div>
            <label htmlFor="username" className="block text-sm font-medium text-ceramic-700 mb-2">
              帳號
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
              placeholder="輸入管理員帳號"
              autoFocus
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-ceramic-700 mb-2">
              密碼
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                placeholder="輸入密碼"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "隱藏密碼" : "顯示密碼"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-ceramic-400 hover:text-ceramic-700 hover:bg-ceramic-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            登入
          </button>
        </form>
      </div>
    </div>
  );
}
